import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useLocationPermission, PermissionStatus } from '@/hooks/useLocationPermission';

interface LocationContextType {
  permissionStatus: PermissionStatus;
  isLoading: boolean;
  isIOSPWA: boolean;
  isAndroidPWA: boolean;
  isPWA: boolean;
  error: string | null;
  requestPermission: () => Promise<boolean>;
  checkPermission: () => Promise<void>;
  canUseLocation: boolean;
  hasCheckedOnce: boolean;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export function LocationProvider({ children }: { children: ReactNode }) {
  const {
    status,
    isLoading,
    isIOSPWA,
    isAndroidPWA,
    isPWA,
    error,
    requestPermission,
    checkPermission,
  } = useLocationPermission();
  
  const [hasCheckedOnce, setHasCheckedOnce] = useState(false);

  // Mark that we've checked at least once after initial load
  useEffect(() => {
    if (!isLoading && !hasCheckedOnce) {
      setHasCheckedOnce(true);
    }
  }, [isLoading, hasCheckedOnce]);

  // Can use location if permission is granted
  const canUseLocation = status === 'granted';

  const value: LocationContextType = {
    permissionStatus: status,
    isLoading,
    isIOSPWA,
    isAndroidPWA,
    isPWA,
    error,
    requestPermission,
    checkPermission,
    canUseLocation,
    hasCheckedOnce,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}
